import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';

const DateChangeReviewModal = ({ show, onHide, milestone, onSuccess }) => {
    const [reviewComment, setReviewComment] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const authInfo = JSON.parse(localStorage.getItem('authInfo'));

    const handleAction = async (action) => {
        setError('');
        setLoading(true);

        try {
            const response = await axios.post(
                `/user/milestone/${milestone._id}/review-date-change`,
                {
                    action: action,
                    reviewComment: reviewComment,
                    userId: authInfo.id // Add this line
                },
                {
                    headers: {
                        'Authorization': `Bearer ${authInfo.token}`
                    }
                }
            );

            if (response.data.success) {
                toast.success(response.data.message);
                onSuccess && onSuccess(response.data.milestone);
                onHide();
                setReviewComment('');
            }
        } catch (error) {
            console.error('Error reviewing date change request:', error);
            setError(error.response?.data?.message || 'Failed to process request');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const calculateDaysDifference = (date1, date2) => {
        const diffTime = Math.abs(new Date(date2) - new Date(date1));
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Review Date Change Request</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                
                <div className="mb-4">
                    <h6>Milestone: {milestone?.name}</h6>
                    <p className="text-muted mb-0">Amount: ${milestone?.amount?.toFixed(2)}</p>
                </div>

                <Row className="mb-4">
                    <Col md={6}>
                        <div className="border rounded p-3">
                            <h6 className="text-muted mb-2">Current Due Date</h6>
                            <div className="h5 mb-0">
                                {milestone?.dueDate ? formatDate(milestone.dueDate) : 'Not set'}
                            </div>
                        </div>
                    </Col>
                    <Col md={6}>
                        <div className="border rounded p-3">
                            <h6 className="text-muted mb-2">Requested New Date</h6>
                            <div className="h5 mb-0 text-warning">
                                {milestone?.dueDateChangeRequest?.requestedDate ? 
                                    formatDate(milestone.dueDateChangeRequest.requestedDate) : 'Not set'}
                            </div>
                            <small className="text-muted">
                                (+{calculateDaysDifference(
                                    milestone?.dueDate, 
                                    milestone?.dueDateChangeRequest?.requestedDate
                                )} days extension)
                            </small>
                        </div>
                    </Col>
                </Row>

                <div className="mb-4">
                    <h6>Seller's Reason:</h6>
                    <div className="bg-light p-3 rounded">
                        <em>"{milestone?.dueDateChangeRequest?.reason || 'No reason provided'}"</em>
                    </div>
                </div>

                <div className="mb-4">
                    <small className="text-muted">
                        Requested by: {milestone?.dueDateChangeRequest?.requestedBy?.name || 'Unknown'} on{' '}
                        {milestone?.dueDateChangeRequest?.requestedAt ? 
                            formatDate(milestone.dueDateChangeRequest.requestedAt) : 'Unknown date'}
                    </small>
                </div>

                <Form.Group className="mb-4">
                    <Form.Label>Your Comments (Optional)</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={reviewComment}
                        onChange={(e) => setReviewComment(e.target.value)}
                        placeholder="Add any comments about your decision..."
                    />
                </Form.Group>

                <div className="d-flex justify-content-end gap-2">
                    <Button variant="secondary" onClick={onHide} disabled={loading}>
                        Cancel
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={() => handleAction('reject')} 
                        disabled={loading}
                    >
                        {loading ? 'Processing...' : 'Reject Request'}
                    </Button>
                    <Button 
                        variant="success" 
                        onClick={() => handleAction('approve')} 
                        disabled={loading}
                    >
                        {loading ? 'Processing...' : 'Approve Request'}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default DateChangeReviewModal;